package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.ResourceVersion
import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/*
 * Group wire types, mirroring openapi.yaml v1.0.0 tag `Groups`.
 *
 *
 * WHY EVERY REQUIRED ENUM HERE CARRIES NO DEFAULT
 *
 * `coerceInputValues` substitutes a property's DECLARED DEFAULT, so an enum without one throws on
 * an unrecognised value instead of quietly becoming `UNKNOWN`. Elsewhere in this module that
 * trade goes the other way: `QrCode.target_type` keeps an UNKNOWN default so a target kind added
 * in a later minor version leaves a shipped client showing an unrecognised code rather than
 * failing to parse the screen it is on.
 *
 * That reasoning does not reach these. The contract is FROZEN at v1.0.0 and every enum below is
 * closed at two, three or four members, so there is no later minor version to be compatible with
 * and nothing to buy with a default. What each of them decides is a PERMISSION or an irreversible
 * action: whether a composer is shown, whether an admin control exists, whether a group is public
 * forever, whether a join request is approved. A value the server did not send is a permission
 * nobody granted, and rendering one is worse than reporting that the response was not
 * understood.
 *
 * The `UNKNOWN` members still exist, because every wire enum in this module has one and because
 * `when` blocks over them stay exhaustive. Under a conforming server they are unreachable, which
 * is the same arrangement `DevicePlatform` has in `Notifications.kt`.
 *
 * Optional properties keep defaults, so an omitted one decodes rather than failing.
 */

// ---------------------------------------------------------------------------------- enums

/**
 * `Group.visibility`, `CreateGroupRequest.visibility`, `GroupInvitePreview.visibility`.
 *
 * IMMUTABLE once a group exists: `UpdateGroupRequest` offers name, description, username and
 * avatar and deliberately does not offer this. Turning a private group public would retroactively
 * expose its Discover area and member list; turning a public one private would strand its
 * username and every printed QR code built on it.
 */
@Serializable
enum class GroupVisibility {
    @SerialName("public") PUBLIC,
    @SerialName("private") PRIVATE,
    @SerialName("unknown") UNKNOWN,
}

/**
 * `Group.role` (the CALLER's role) and `GroupMember.role`.
 *
 * Not the type used to CHANGE a role: `updateGroupMemberRole` accepts only `admin` and `member`,
 * and that narrower set is [GroupMemberRoleAssignment]. Reusing this one there would put `owner`
 * in reach of an endpoint the contract keeps it out of, since ownership moves only through
 * `transferGroupOwnership`.
 */
@Serializable
enum class GroupRole {
    @SerialName("owner") OWNER,
    @SerialName("admin") ADMIN,
    @SerialName("member") MEMBER,
    @SerialName("unknown") UNKNOWN,
}

/** `GroupSettings.discover_posting`, `.chat_posting` and `.invite_permission`. */
@Serializable
enum class GroupPostingPolicy {
    @SerialName("all_members") ALL_MEMBERS,
    @SerialName("admins_only") ADMINS_ONLY,
    @SerialName("unknown") UNKNOWN,
}

/** `GroupInvitePreview.action` -- what the button on the preview screen actually does. */
@Serializable
enum class GroupInviteAction {
    @SerialName("join") JOIN,
    @SerialName("request_access") REQUEST_ACCESS,
    @SerialName("already_member") ALREADY_MEMBER,
    @SerialName("request_pending") REQUEST_PENDING,
    @SerialName("unknown") UNKNOWN,
}

/**
 * `GroupJoinResult.status`. `joined` accompanies the 200, `pending_approval` the 202.
 *
 * The distinction is the whole result: on `joined` the client opens the group, on
 * `pending_approval` it shows that an admin has to act. Guessing sends someone into a group they
 * are not a member of.
 */
@Serializable
enum class GroupJoinStatus {
    @SerialName("joined") JOINED,
    @SerialName("pending_approval") PENDING_APPROVAL,
    @SerialName("unknown") UNKNOWN,
}

/** `GroupJoinRequest.status`. */
@Serializable
enum class GroupJoinRequestStatus {
    @SerialName("pending") PENDING,
    @SerialName("approved") APPROVED,
    @SerialName("rejected") REJECTED,
    @SerialName("unknown") UNKNOWN,
}

/** The `decision` in the `resolveGroupJoinRequest` body. */
@Serializable
enum class GroupJoinDecision {
    @SerialName("approve") APPROVE,
    @SerialName("reject") REJECT,
    @SerialName("unknown") UNKNOWN,
}

/**
 * The `role` in the `updateGroupMemberRole` body: `admin` or `member`, and nothing else.
 *
 * Separate from [GroupRole] because the contract declares it separately. It is also the safer
 * shape: `conversation_members` has `CHECK (role IN ('owner','admin','member'))` and a partial
 * unique index allowing one owner per conversation, so a request that coerced to `unknown` would
 * pass every Kotlin check and then fail at the UPDATE as a 500 rather than the 400 a malformed
 * body earns. Hence no default here, exactly as `DevicePlatform` does it in `Notifications.kt`.
 */
@Serializable
enum class GroupMemberRoleAssignment {
    @SerialName("admin") ADMIN,
    @SerialName("member") MEMBER,
    @SerialName("unknown") UNKNOWN,
}

// -------------------------------------------------------------------------------- settings

/**
 * `GroupSettings` -- embedded in every [Group] and returned by `getGroupSettings` /
 * `updateGroupSettings`.
 *
 * This type is NOT the update body; [GroupSettingsUpdate] is. All four properties are `required`
 * and none carries a default, so a response missing one is a decode failure rather than a
 * permission surface assembled out of guesses.
 *
 * [invitePermission] is read-only in practice. `GroupSettingsUpdate` cannot set it, and the
 * contract states its value as a function of visibility: "Public groups use all_members; private
 * groups use admins_only" -- matching DECISIONS.md §7, where public-group invitations may be
 * shared by all members and private-group invitations only by Owner or Admin. The server derives
 * it; it is not a stored column.
 *
 * [version] belongs to the settings alone. `Group` has no version, so renaming a group is not
 * under optimistic concurrency and must not consume this counter.
 */
@Serializable
data class GroupSettings(
    @SerialName("discover_posting") val discoverPosting: GroupPostingPolicy,
    @SerialName("chat_posting") val chatPosting: GroupPostingPolicy,
    /** Derived from visibility by the server; no operation can change it. */
    @SerialName("invite_permission") val invitePermission: GroupPostingPolicy,
    @SerialName("version") val version: ResourceVersion,
)

/**
 * `GroupSettingsUpdate` -- the `updateGroupSettings` body. `minProperties: 2` with `version`
 * required, so a conforming body carries the version and at least one policy.
 *
 * The two policies are OPTIONAL, and absence means "leave unchanged". That forces them to be
 * nullable with a null default, and it brings a trap that no type in this module can close:
 *
 *   `coerceInputValues` turns an UNRECOGNISED enum value into the declared default, so
 *   `{"chat_posting":"everyone","version":3}` decodes as `chatPosting = null` -- identical to
 *   omitting the field. Decoded alone, this type cannot tell "change it to something I do not
 *   understand" from "do not change it", and answering 200 with the old value to a client that
 *   asked for a new one is exactly the silent-success failure the rest of this module avoids.
 *
 * SO THE SERVER MUST NOT DECIDE WHAT CHANGED FROM NULL-NESS. It already holds the raw body for
 * the idempotency hash; the set of keys actually present is what says which fields were sent, and
 * a present key that decoded to null is `validation_failed`, not a no-op.
 */
@Serializable
data class GroupSettingsUpdate(
    @SerialName("discover_posting") val discoverPosting: GroupPostingPolicy? = null,
    @SerialName("chat_posting") val chatPosting: GroupPostingPolicy? = null,
    @SerialName("version") val version: ResourceVersion,
)

// ----------------------------------------------------------------------------------- group

/**
 * `Group` -- `createGroup` (201), `getGroup`, `updateGroup`, `getPublicGroupByUsername` and every
 * item of a [GroupPage].
 *
 * [id] and [conversationId] are two different values, and the contract requires both. The group is
 * its own resource; the chat underneath it is a conversation, addressed by the conversation
 * endpoints and the `conversation:{id}` realtime channel. Do not pass one where the other is
 * expected on the assumption they match, because nothing in the contract says they do.
 *
 * [name] and [avatarUrl] are the underlying conversation's title and avatar. That is deliberate on
 * the server side: a second copy on the group would let the chat list and the group screen show
 * different names after a rename, with nothing to say which is right.
 *
 * [username] and [publicUrl] are non-null only for public groups; private groups are reached by
 * invite link. [publicUrl] is derived from the username, not stored, so it changes when the
 * username does.
 *
 * [role] is the CALLER's role in this group, not a property of the group.
 */
@Serializable
data class Group(
    @SerialName("id") val id: Uuid,
    /** The chat. Distinct from [id]; use it for the conversation and realtime endpoints. */
    @SerialName("conversation_id") val conversationId: Uuid,
    /** `minLength: 1`, `maxLength: 100`. */
    @SerialName("name") val name: String,
    /** `maxLength: 500`. */
    @SerialName("description") val description: String? = null,
    @SerialName("avatar_url") val avatarUrl: String? = null,
    @SerialName("visibility") val visibility: GroupVisibility,
    /** Public groups only. */
    @SerialName("username") val username: String? = null,
    /** Public groups only, e.g. `https://swetgram.com/g/community`. */
    @SerialName("public_url") val publicUrl: String? = null,
    /** The CALLER's role. Never treat [GroupRole.UNKNOWN] as granting anything. */
    @SerialName("role") val role: GroupRole,
    /** True only when the caller is a live member. Public previews deliberately return false. */
    @SerialName("is_member") val isMember: Boolean = true,
    /** `minimum: 1`, `maximum: 500`. A 501st join is `group_full` (409). */
    @SerialName("member_count") val memberCount: Int,
    @SerialName("settings") val settings: GroupSettings,
    @SerialName("created_at") val createdAt: Timestamp,
)

/** `GroupPage` -- the `listGroups` 200 body. */
@Serializable
data class GroupPage(
    @SerialName("items") val items: List<Group> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo,
)

/**
 * `CreateGroupRequest` -- the `createGroup` body. Requires a verified email
 * (`x-requires-verified-email: true`).
 *
 * [visibility] carries no default, and of everything in this file that is the one that matters
 * most: it can never be changed afterwards. A body whose visibility did not decode must be a 400,
 * not a group that is permanently public because nobody looked.
 *
 * [username] is required when [visibility] is [GroupVisibility.PUBLIC] and must be absent or null
 * when it is private -- the contract says so as an `if`/`then` pair, which kotlinx cannot enforce,
 * so the server validates it. Public-group usernames live in their own namespace, separate from
 * user usernames.
 *
 * [initialMemberIds] is `maxItems: 100`, `uniqueItems: true`. A `List` cannot express uniqueness;
 * duplicates would collide on the `conversation_members` primary key, so the server de-duplicates
 * or rejects rather than letting an INSERT decide.
 */
@Serializable
data class CreateGroupRequest(
    @SerialName("name") val name: String,
    @SerialName("description") val description: String? = null,
    /** Immutable after creation. No default: an unrecognised value must be a 400. */
    @SerialName("visibility") val visibility: GroupVisibility,
    /** Required for public groups; unavailable for private groups. */
    @SerialName("username") val username: String? = null,
    @SerialName("avatar_media_id") val avatarMediaId: Uuid? = null,
    /** `maxItems: 100`, `uniqueItems: true`. */
    @SerialName("initial_member_ids") val initialMemberIds: List<Uuid> = emptyList(),
)

/**
 * `UpdateGroupRequest` -- the `updateGroup` body. `minProperties: 1`; everything is optional and
 * absence means "leave unchanged".
 *
 * TRAP, and it is a property of `ProtocolJson` rather than of this type: `explicitNulls = false`
 * drops null properties when ENCODING, so `UpdateGroupRequest(description = null)` serializes to
 * `{}` and clears nothing. All three nullable fields are `oneOf [X, null]` in the contract, where
 * null means "remove the description / username / avatar" -- a meaning this type cannot transmit.
 *
 * A caller that needs to CLEAR a field must build the JSON object itself. Sending an empty string
 * instead is not the same request and the server is entitled to reject it.
 */
@Serializable
data class UpdateGroupRequest(
    @SerialName("name") val name: String? = null,
    @SerialName("description") val description: String? = null,
    /** Public groups only. Changes follow the same 30-day reservation rules as user usernames. */
    @SerialName("username") val username: String? = null,
    @SerialName("avatar_media_id") val avatarMediaId: Uuid? = null,
)

// --------------------------------------------------------------------------------- members

/** `GroupMember` -- an item of [GroupMemberPage]. All three properties are required. */
@Serializable
data class GroupMember(
    @SerialName("user") val user: UserPublic,
    @SerialName("role") val role: GroupRole,
    @SerialName("joined_at") val joinedAt: Timestamp,
)

/** `GroupMemberPage` -- the `listGroupMembers` 200 body. */
@Serializable
data class GroupMemberPage(
    @SerialName("items") val items: List<GroupMember> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo,
)

/**
 * The `updateGroupMemberRole` body.
 *
 * Owner is not reachable here by design; `transferGroupOwnership` is the only path to it, and the
 * schema guarantees a single owner per group.
 */
@Serializable
data class UpdateGroupMemberRoleRequest(
    @SerialName("role") val role: GroupMemberRoleAssignment,
)

/**
 * The `transferGroupOwnership` body. Answers 204; there is no response type.
 *
 * The current owner becomes an admin, which is what makes `leaveGroup` possible for them --
 * leaving while still the owner is `ownership_transfer_required` (409).
 */
@Serializable
data class TransferGroupOwnershipRequest(
    @SerialName("new_owner_user_id") val newOwnerUserId: Uuid,
)

/*
 * `removeGroupMember` takes its `remove` / `remove_and_ban` choice as a REQUIRED QUERY PARAMETER,
 * not a body, so it is deliberately not modelled here: a @Serializable enum for a value that never
 * appears in JSON invites someone to encode it into one.
 */

// ------------------------------------------------------------------------------------ bans

/** `GroupBan` -- an item of [GroupBanPage]. */
@Serializable
data class GroupBan(
    @SerialName("user") val user: UserPublic,
    @SerialName("banned_by") val bannedBy: UserPublic,
    @SerialName("created_at") val createdAt: Timestamp,
)

/** `GroupBanPage` -- the `listGroupBans` 200 body. */
@Serializable
data class GroupBanPage(
    @SerialName("items") val items: List<GroupBan> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo,
)

// --------------------------------------------------------------- invite links and joining

/**
 * `InviteLink` -- the `createOrRotateInviteLink` 200 body, which both creates and rotates.
 *
 * [token] is the credential: anyone holding it can join a public group, or raise a join request
 * for a private one. Treat the whole object as a secret, keep it out of logs, and expect a
 * previously shared link to stop working the moment this endpoint is called again.
 *
 * There is no expiry on the wire. The server stores one, unset in v1, so links do not expire yet.
 */
@Serializable
data class InviteLink(
    /** e.g. `https://swetgram.com/join/example-token`. */
    @SerialName("url") val url: String,
    @SerialName("token") val token: String,
    @SerialName("created_at") val createdAt: Timestamp,
)

/**
 * `GroupInvitePreview` -- the `previewGroupInvite` 200 body, shown BEFORE joining.
 *
 * Note what it does not carry, and why it must not be widened to carry it: there is no
 * `conversation_id` and no member list. The viewer is not a member yet, and a conversation id is
 * the realtime channel name (`conversation:{id}`) -- handing it out before a private group's
 * admins have approved anyone would leak the address of a chat they have not been let into.
 *
 * [action] is what the button does, and it already accounts for the viewer: `already_member` and
 * `request_pending` exist so a second tap on an old link does not create a duplicate request.
 */
@Serializable
data class GroupInvitePreview(
    @SerialName("group_id") val groupId: Uuid,
    @SerialName("name") val name: String,
    @SerialName("description") val description: String? = null,
    @SerialName("avatar_url") val avatarUrl: String? = null,
    @SerialName("visibility") val visibility: GroupVisibility,
    @SerialName("member_count") val memberCount: Int,
    @SerialName("action") val action: GroupInviteAction,
)

/**
 * `GroupJoinResult` -- `actOnGroupInvite`, 200 when the caller joined a public group and 202 when
 * a private group's join request was created.
 *
 * [group] is populated only on the 200. On a 202 the caller is not a member, so there is no
 * `Group` to send: `role`, `settings` and `member_count` all describe a membership they do not
 * have. [status] is the property to branch on, not the presence of [group].
 */
@Serializable
data class GroupJoinResult(
    @SerialName("status") val status: GroupJoinStatus,
    @SerialName("group_id") val groupId: Uuid,
    /** Present only when [status] is [GroupJoinStatus.JOINED]. */
    @SerialName("group") val group: Group? = null,
)

/** `GroupJoinRequest` -- an item of [GroupJoinRequestPage]. */
@Serializable
data class GroupJoinRequest(
    @SerialName("id") val id: Uuid,
    @SerialName("group_id") val groupId: Uuid,
    @SerialName("user") val user: UserPublic,
    @SerialName("status") val status: GroupJoinRequestStatus,
    @SerialName("created_at") val createdAt: Timestamp,
)

/** `GroupJoinRequestPage` -- the `listGroupJoinRequests` 200 body. Private groups only. */
@Serializable
data class GroupJoinRequestPage(
    @SerialName("items") val items: List<GroupJoinRequest> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo,
)

/** The `resolveGroupJoinRequest` body. Answers 204; there is no response type. */
@Serializable
data class ResolveGroupJoinRequestRequest(
    @SerialName("decision") val decision: GroupJoinDecision,
)

// ---------------------------------------------------------------------------------- search

/** `SearchResponse` groups its results by kind; all three arrays are required. */
@Serializable
data class SearchResponse(
    @SerialName("people") val people: List<UserPublic> = emptyList(),
    @SerialName("groups") val groups: List<Group> = emptyList(),
    @SerialName("conversations") val conversations: List<Conversation> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo? = null,
)
